import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable, Subject, Subscriber, Subscription, of } from 'rxjs';
import { debounceTime, filter, share, switchMap, tap } from 'rxjs/operators';
import { BaseComponent } from '../../base-components/base.component';
import { AutoCompleteSelectedItem } from './autocomplete.model';
import { AutoCompleteService } from './autocomplete.service';

@Component({
    selector: 'Autocomplete',
    templateUrl: './autocomplete.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutoCompleteComponent),
            multi: true
        },
        AutoCompleteService
    ]
})
export class AutoCompleteComponent extends BaseComponent implements ControlValueAccessor {
    @Input() controlIdentifier: string;
    @Input() delayTime = 350;
    @Input() disabled = false;
    @Input() itemNotFoundMessage = 'Item Not Found';
    @Input() minLength = 2;
    @Input() parentValue: number;
    @Input() secondaryText: string;
    @Input() serviceIdPath: string;
    @Input() servicePath: string;
    @Input() showItemNotFoundMessage: boolean;
    @Input() text = 'text';
    @Input() value = 'value';

    public errors: string[] = new Array<string>();
    public initComplete = new Subject<boolean>();
    public resultsModel: any[];
    public selectedItem$: Observable<any>;
    private _displayValue: string;
    private initServiceSubscription: Subscription;
    private itemKeyIsZero = 'The selected item key cannot be zero';
    private previousAutoSelectedValue: string;
    private selectedItem: Subscriber<any>;
    private typeAhead = new Subject<string>();
    private typeAheadSubscription: Subscription;

    onChange: any = () => { };
    onTouched: any = () => { };

    constructor(
        private autocompleteService: AutoCompleteService
    ) {
        super();
    }

    ngOnInit() {
        this.selectedItem$ = new Observable(o => this.selectedItem = o).pipe(share());
        this.handleTypingIntoInput();
        this.autocompleteService.servicePath = this.servicePath;

        if (this.serviceIdPath !== null && this.serviceIdPath !== undefined) {
            this.autocompleteService.serviceIdPath = this.serviceIdPath;
        }
    }

    ngOnDestroy() {
        if (this.initServiceSubscription) {
            this.initServiceSubscription.unsubscribe();
        }

        if (this.typeAheadSubscription) {
            this.typeAheadSubscription.unsubscribe();
        }
    }

    get displayValue() {
        return this._displayValue;
    }

    set displayValue(val) {
        this._displayValue = val;
        this.onChange(val);
        this.onTouched();
    }

    autocompleteSelected(result: any) {
        this.setValues(result[this.value], result[this.text]);
        this.resultsModel = null; // item was selected, clear the list
    }

    clear() {
        this.displayValue = null;
        this.resultsModel = null;
    }

    displayValueChanged(value: string) {
        this.errors.length = 0;

        if (value.length === 0) {
            this.resetValues();
        }
        if (value.length < this.minLength) {
            this.setValues(null, this.displayValue);

        } else {
            this.displayValue = value;
            this.typeAhead.next(value);

        }
    }

    getResultText(result: any) {
        const resultsSecondaryText = this.secondaryText ? ` (${result[this.secondaryText]})` : '';
        return `${result[this.text]}${resultsSecondaryText}`;
    }

    handleValidationResult(error) {
        this.resetValues();

        this.errors = new Array<string>();
        let errorObject: any;

        if (this.isJsonString(error.error)) {
            errorObject = JSON.parse(error.error);

            errorObject.validationResults.forEach(error => {
                this.errors.push(error.errorMessage);
            });
        } else {
            this.errors.push(error.message);
        }
    }

    hasErrors() {
        return this.errors.length > 0;
    }

    init(value: number, text: string) {
        if (this.serviceIdPath === null || this.serviceIdPath === undefined) {
            throw new Error('AutoComplete is unable to init() because the ServiceIdPath variable has not been set');
        }

        this.errors.length = 0;

        if (text !== null) {
            this.displayValue = text;
        }

        if (value !== null && value !== 0) {
            this.retrieveDataById(value);
        }
    }

    registerOnChange(fn) {
        this.onChange = fn;
    }

    registerOnTouched(fn) {
        this.onTouched = fn;
    }

    writeValue(value) {
        if (value) {
            this.init(null, value);
        }
    }

    private handleAutocompleteResults(results: any) {
        // if (results.length === 1) { // auto select the item if only one result was returned
        //    let autoSelectedValue = results[0][this.text];
        //    if (autoSelectedValue != this.previousAutoSelectedValue) {
        //        this.setValues(results[0][this.value], results[0][this.text]);
        //        this.previousAutoSelectedValue = autoSelectedValue;
        //    }
        // }
        // else
        if (results.length >= 1) { // multiple results were found so clear the selected value
            this.setValues(null, this.displayValue);
        } else { // no results returned
            if (this.showItemNotFoundMessage && this.showItemNotFoundMessage == true) {
                this.errors.push(this.itemNotFoundMessage);
            }
            this.setValues(null, this.displayValue);
        }
    }

    private handleTypingIntoInput() {
        this.typeAheadSubscription = this.typeAhead.asObservable()
            .pipe(
                debounceTime(this.delayTime),
                switchMap(() => !!this.parentValue ?
                    this.autocompleteService.getDataByParent(this.parentValue, this.displayValue) :
                    this.autocompleteService.getDataByText(this.displayValue)),
                filter(results => Array.isArray(results)),
                tap(results => this.resultsModel = results)
            )
            .subscribe(
                results => this.handleAutocompleteResults(results),
                error => this.handleValidationResult(error)
            );
    }

    private isJsonString(str) {
        if (str === undefined || str === null) {
            return false;
        }

        try {
            JSON.parse(str);
        } catch (e) {
            return false;
        }

        return true;
    }

    private resetValues() {
        if (this.selectedItem !== null && this.selectedItem !== undefined) {
            const item: AutoCompleteSelectedItem = { identifier: this.controlIdentifier, selectedValue: null, selectedText: null };
            this.selectedItem.next(item);
        }

        this.displayValue = null;
        this.resultsModel = null;
    }

    private retrieveDataById(id: number) {
        this.initServiceSubscription = of(id)
            .pipe(
                switchMap(val => this.autocompleteService.getDataById(val)),
                filter(results => !!results),
                tap(results => this.setValues(results[this.value], results[this.text]))
            )
            .subscribe(
                () => this.initComplete.next(true),
                error => this.handleValidationResult(error)
            );
    }

    private setValues(value: number, text: string) {

        if (value === 0) {
            this.resetValues();
            this.errors.push(this.itemKeyIsZero);
        }

        if (this.resultsModel !== null && this.resultsModel !== undefined) {
            if (this.resultsModel.constructor === Array) {
                if (this.resultsModel.length < 1) {
                    this.resultsModel = null;
                }
            } else {
                this.resultsModel = null;
            }
        }

        this.displayValue = text;

        if (this.selectedItem !== null && this.selectedItem !== undefined) {
            const item: AutoCompleteSelectedItem = { identifier: this.controlIdentifier, selectedValue: value, selectedText: text };
            this.selectedItem.next(item);
        }

    }
}
